import { IAggregatedColumn, getBindingForRow } from "../../../utilities/AggregatedColumnHelper";
import { AGGREGATE_SOURCE_LIST_ID } from "./aggregateRowFields";

/**
 * Detects merged columns that are blank across every loaded row so the grid can
 * auto-deselect them. Emptiness mirrors what renderAggregatedCell paints as blank:
 * a row contributes a value only through its own library's binding, and a cell
 * counts as empty when the wire value carries no visible content.
 *
 * Verdicts are only as good as the loaded window — callers own the stability
 * rules (only evaluate on loaded rows, treat "seen non-empty" as sticky for the
 * session) so paging/filtering/refresh can never make a populated column vanish.
 */

/** True when a RenderListDataAsStream cell value renders as visually blank. */
export const isEmptyCellValue = (value: any): boolean => {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === "string") {
        return value.trim().length === 0;
    }
    if (Array.isArray(value)) {
        return value.every(isEmptyCellValue);
    }
    if (typeof value === "object") {
        // Multi-value shapes (user/lookup/taxonomy/URL): blank unless some label key
        // carries content — same keys the renderers and valueLabel read.
        const label = value.Label ?? value.lookupValue ?? value.title ?? value.value ?? value.Url;
        return label === null || label === undefined || String(label).trim().length === 0;
    }
    return false; // numbers/booleans always render (0 and false are values)
};

/**
 * Column keys with no visible value in ANY loaded row. A row whose library has no
 * binding for the column contributes nothing (that cell renders blank); a column
 * bound in no loaded row's library is therefore empty too.
 */
export const getEmptyColumnKeys = (mergedColumns: IAggregatedColumn[], rows: any[]): Set<string> => {
    const emptyKeys = new Set<string>();
    for (const column of mergedColumns ?? []) {
        let hasValue = false;
        for (const row of rows ?? []) {
            const binding = getBindingForRow(column, row?.[AGGREGATE_SOURCE_LIST_ID]);
            if (binding && !isEmptyCellValue(row[binding.internalName])) {
                hasValue = true;
                break;
            }
        }
        if (!hasValue) {
            emptyKeys.add(column.key);
        }
    }
    return emptyKeys;
};
